import * as React from 'react';
import { Button, Toggle, Icon, FieldMessage, FieldLabel } from '@teamsnap/teamsnap-ui';
import { toCamelCase } from 'frontend-toolkit';
import { TINY_MCE_API_KEY } from 'core/constants';
import { Editor } from '@tinymce/tinymce-react';
import FormElementHook from 'components/form/form-element-hook';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@apollo/client';
import * as UpdateFormFieldDefinition from 'core/gql/mutations/update-form-field-definition';
import * as GqlOrganizationFormFieldDefinitions from 'core/gql/queries/get-organization-form-field-definitions';
import * as CreateFormFieldDefinition from 'core/gql/mutations/create-form-field-definition';
import * as CreateFormField from 'core/gql/mutations/create-form-field';
import * as UpdateFormField from 'core/gql/mutations/update-form-field';
import * as CreateFormFieldVisibilityRule from 'core/gql/mutations/create-form-field-visibility-rule';
import * as UpdateFormFieldVisibilityRule from 'core/gql/mutations/update-form-field-visibility-rule';
import * as DeleteFormFieldVisibilityRule from 'core/gql/mutations/delete-form-field-visibility-rule';
import * as RegistrationFormVisibilityRules from 'core/gql/queries/registration-form-visibility-rules';
import * as RegistrationFormFieldVisibilityRules from 'core/gql/queries/registration-form-field-visibility-rules';
import * as DeleteRegistrationFormAdjustment from 'core/gql/mutations/delete-registration-form-adjustment';
import * as RegistrationFormAdjustments from 'core/gql/queries/registration-form-adjustments';
import * as CreateRegistrationFormAdjustment from 'core/gql/mutations/create-form-adjustment';
import * as UpdateRegistrationFormAdjustment from 'core/gql/mutations/update-form-adjustment';
import * as FormAdjustmentDefinitions from 'core/gql/queries/form-adjustment-definitions';

import { reactiveFeedbackMessage } from 'core/apollo-cache';
import * as GetForm from 'core/gql/queries/show-form';
import { FieldType } from './types';
import AdditionalQuestionConditionalLogic from './AdditionalQuestionConditionalLogic/AdditionalQuestionConditionalLogic';
import AddOnFees from './AddOnFees/AddOnFees';
import WarningModal from './WarningModal';

import './AdditionalQuestionForm.scss';

const CreateElement = FormElementHook as new () => FormElementHook;

type Props = {
  orgId: number;
  formId?: number;
  question?: FieldType;
  onCancel?: () => void;
  onSave?: (field: FieldType) => void;
  addQuestionToRow?: number;
  disableEditingAttributes?: boolean;
};

enum FormFieldReponseType {
  CREATE = 'createRegistrationFormField',
  UPDATE = 'updateRegistrationFormField',
}

const showFeedback = () => {
  reactiveFeedbackMessage({
    message: 'Oh no! It looks like something went wrong. Please try again or contact TeamSnap Support for help.',
    isError: true,
    autoclose: true,
  });
};

const checkFormFieldResultResponse = (response, type: FormFieldReponseType) => {
  if (!response?.data?.[type]?.id) {
    showFeedback();
    return;
  } else {
    return response?.data?.[type];
  }
};

const mapDefinitionRequestBody = (orgId, question, formValues) => ({
  target: 'participant',
  organization_id: +orgId,
  label: formValues.label,
  order: question.order ?? 0,
  helpText: formValues.helpText,
  multiselect: question.isMultiSelect,
  id: question.form_field_definition_id,
  required_default: question.isRequired,
  type: question.type ?? formValues.type,
  options: formValues.options ? formValues.options.split('\n') : [],
  name: question?.name !== 'first_name' && question?.name !== 'last_name' ? formValues.name : question.name,
});

const mapFormFieldRequestBody = (formId, question, formValues, definitionId) => ({
  ...question,
  formId,
  column: 0,
  label: formValues.label,
  enabled: question.enabled,
  required: question.isRequired,
  helpText: formValues.helpText,
  formFieldDefinitionId: definitionId,
  target: question.target ?? 'participant',
  multiselect: question.isMultiSelect ?? formValues.isMultiSelect,
  options: formValues.options ? formValues.options.split('\n') : [],
});

const getReadableName = (question) => {
  if (question.name === 'first_name' || question.name === 'last_name') {
    return `${question.name === 'first_name' ? 'First Name' : 'Last Name'} (${question.target})`;
  }
  return question.name;
};

const AdditionalQuestionForm = ({
  orgId,
  question,
  onCancel,
  onSave,
  formId,
  addQuestionToRow,
  disableEditingAttributes = false,
}: Props) => {
  const [helpTextError, setHelpTextError] = React.useState(false);
  const refetchDefinitions = {
    refetchQueries: [{ query: GqlOrganizationFormFieldDefinitions.QUERY, variables: { organization_id: orgId } }],
  };

  const refetchForm = {
    refetchQueries: [{ query: GetForm.QUERY, variables: { formId: formId, organizationId: orgId } }],
  };

  const refetchRules = {
    refetchQueries: [
      { query: RegistrationFormVisibilityRules.QUERY, variables: { formFieldId: +question?.id, formId: +formId } },
    ],
  };

  const refetchFees = {
    refetchQueries: [{ query: RegistrationFormAdjustments.QUERY, variables: { formId: +formId } }],
  };

  const [updateFormFieldDefinition] = useMutation(UpdateFormFieldDefinition.MUTATION, refetchDefinitions);
  const [createFormFieldDefinition] = useMutation(CreateFormFieldDefinition.MUTATION, refetchDefinitions);

  const [createFormField] = useMutation(CreateFormField.MUTATION, refetchForm);
  const [updateFormField] = useMutation(UpdateFormField.MUTATION, refetchForm);

  const [createFormFieldVisibilityRule] = useMutation(CreateFormFieldVisibilityRule.MUTATION, refetchRules);
  const [updateFormFieldVisibilityRule] = useMutation(UpdateFormFieldVisibilityRule.MUTATION, refetchRules);
  const [deleteFormFieldVisibilityRule] = useMutation(DeleteFormFieldVisibilityRule.MUTATION, refetchRules);

  const [createRegistrationFormAdjustment] = useMutation(CreateRegistrationFormAdjustment.MUTATION, refetchFees);
  const [updateRegistrationFormAdjustment] = useMutation(UpdateRegistrationFormAdjustment.MUTATION, refetchFees);
  const [deleteRegistrationFormAdjustment] = useMutation(DeleteRegistrationFormAdjustment.MUTATION, refetchFees);

  const visibilityRules =
    useQuery(RegistrationFormFieldVisibilityRules.QUERY, {
      variables: { formFieldId: +question?.id, formId: +formId },
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'network-only',
    })?.data?.registrationFormFieldVisibilityRules || [];

  const questionDefinitions = useQuery(GqlOrganizationFormFieldDefinitions.QUERY, {
    variables: { organization_id: +orgId },
  });

  const formFees =
    useQuery(RegistrationFormAdjustments.QUERY, {
      variables: { formId: +formId },
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'network-only',
    })?.data?.registrationFormAdjustments || [];

  const formAdjustmentDefinitions =
    useQuery(FormAdjustmentDefinitions.QUERY, {
      variables: { organizationId: +orgId },
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'network-only',
    })?.data?.formAdjustmentDefinitions || [];

  const [isMultiSelect, setIsMultiselect] = React.useState(question?.multiselect ?? false);
  const [isRequired, setIsRequired] = React.useState<boolean>(question?.required ?? false);
  const [showWarning, setShowWarning] = React.useState(false);

  const questionForm = useForm({
    defaultValues: {
      helpText: question?.helpText ?? '',
      name: question?.name ?? '',
      label: question?.label ?? '',
      type: question?.type ?? 'text',
      options: question?.options.join('\n') ?? '',
    },
  });

  const {
    handleSubmit,
    register,
    watch,
    setValue,
    formState: { dirty },
  } = questionForm;

  React.useEffect(() => {
    register('helpText');
    if (question) {
      setValue('type', question.type);
      setValue('label', question.label);
      setValue('helpText', question.helpText);
      setValue('name', getReadableName(question));
      setValue('options', question.options.join('\n'));
    }
  }, [question]);

  const watchType = watch('type');
  const hasOptions = watchType === 'select';
  const classPrefix = question ? 'edit' : 'add';

  const saveQuestion = async (values) => {
    const cleanText = values.helpText?.replace(/(<([^>]+)>)/gi, '');
    setHelpTextError(cleanText?.length > 400);
    if (cleanText?.length > 400) return;

    try {
      if (question) {
        if (values.saveToLibrary) {
          const variables = mapDefinitionRequestBody(+orgId, { ...question, isMultiSelect, isRequired }, values);
          await updateFormFieldDefinition({ variables });
        }

        let response;
        let type = FormFieldReponseType.UPDATE;

        if (question.id) {
          response = await updateFormField({
            variables: mapFormFieldRequestBody(
              formId,
              { ...question, isMultiSelect, isRequired },
              values,
              question.form_field_definition_id
            ),
          });
        } else {
          type = FormFieldReponseType.CREATE;
          response = await createFormField({
            variables: mapFormFieldRequestBody(
              formId,
              { ...question, isMultiSelect, isRequired, row: addQuestionToRow },
              values,
              question.form_field_definition_id
            ),
          });
        }

        const formFieldResult = checkFormFieldResultResponse(response, type);

        if (values.logic && values.logic.length > 0) {
          const rulesToDelete = visibilityRules.filter((r) => !values.logic.some((v) => +r.id === +v.id));
          rulesToDelete.forEach(async (rule) => {
            await deleteFormFieldVisibilityRule({
              variables: {
                formFieldId: question.id,
                formId: formId,
                id: rule.id,
              },
            });
          });

          values.logic.forEach(async (logic, index) => {
            if (logic.id !== '') {
              await updateFormFieldVisibilityRule({
                variables: {
                  id: +logic.id,
                  formFieldId: question.id,
                  formId: formId,
                  answerToMatch: logic.answer,
                  chainingComparison: logic.comparison,
                  comparison: logic.condition,
                  order: index,
                  targetFormFieldId: +logic.question,
                },
              });
            } else {
              await createFormFieldVisibilityRule({
                variables: {
                  formFieldId: question.id,
                  formId: formId,
                  answerToMatch: logic.answer,
                  chainingComparison: logic.comparison,
                  comparison: logic.condition,
                  order: index,
                  targetFormFieldId: +logic.question,
                },
              });
            }
          });
        } else if (!values.logic && visibilityRules.length > 0) {
          visibilityRules.forEach(async (rule) => {
            await deleteFormFieldVisibilityRule({
              variables: {
                formFieldId: question.id,
                formId: formId,
                id: rule.id,
              },
            });
          });
        }

        if (values.fees && values.fees.length > 0) {
          const feesToDelete = formFees.filter(
            (r) => r.formFieldId === question.id && !values.fees.some((v) => +r.id === +v.id)
          );
          feesToDelete.forEach(async (fee) => {
            await deleteRegistrationFormAdjustment({
              variables: {
                formId: +formId,
                id: +fee.id,
              },
            });
          });

          values.fees.forEach(async (fee) => {
            if (fee.id && fee.fee === '') {
              await deleteRegistrationFormAdjustment({
                variables: {
                  formId: +formId,
                  id: +fee.id,
                },
              });
            } else if (fee.id && fee.id !== '' && fee.fee === fee.id) {
              const prevFee = formFees.filter((f) => f.id === +fee.id).at(-1);
              await updateRegistrationFormAdjustment({
                variables: {
                  id: +fee.id,
                  formId: +formId,
                  amount: +prevFee.amount,
                  formAdjustmentDefinitionId: +prevFee.formAdjustmentDefinition.id,
                  formFieldId: +question.id,
                  maxAmount: +prevFee.maxAmount,
                  maxQuantity: +fee.maxQuantity,
                  answerToMatch: fee.option,
                },
              });
            } else if (fee.id && fee.id !== '' && fee.fee !== fee.id) {
              const feeDefinition = formAdjustmentDefinitions.find((d) => +d.id === +fee.fee);
              await updateRegistrationFormAdjustment({
                variables: {
                  id: +fee.id,
                  formId: +formId,
                  amount: +feeDefinition.amount,
                  formAdjustmentDefinitionId: +feeDefinition.id,
                  formFieldId: +question.id,
                  maxAmount: +feeDefinition.maxAmount,
                  maxQuantity: +fee.maxQuantity,
                  answerToMatch: fee.option,
                },
              });
            } else if (fee.fee !== '' && fee.maxQuantity !== '') {
              const feeDefinition =
                formAdjustmentDefinitions.find((d) => +d.id === +fee.fee) ??
                formFees.find((d) => +d.id === +fee.fee).formAdjustmentDefinition;

              await createRegistrationFormAdjustment({
                variables: {
                  formId: +formId,
                  amount: +feeDefinition.amount,
                  formAdjustmentDefinitionId: +feeDefinition.id,
                  formFieldId: +question.id,
                  maxAmount: +feeDefinition.maxAmount,
                  maxQuantity: +fee.maxQuantity,
                  answerToMatch: fee.option,
                  enabled: true,
                },
              });
            }
          });
        } else if (!values.fees && formFees.length > 0) {
          formFees
            .filter((r) => r.formFieldId === question.id)
            .forEach(async (fee) => {
              await deleteRegistrationFormAdjustment({
                variables: {
                  formId: +formId,
                  id: +fee.id,
                },
              });
            });
        }

        if (onSave) {
          onSave({
            ...question,
            ...formFieldResult,
          });
        }

        reactiveFeedbackMessage({
          message: 'Question Updated',
          isError: false,
          autoclose: true,
        });
      } else {
        const createdQuestionDefinition = await createFormFieldDefinition({
          variables: mapDefinitionRequestBody(+orgId, { ...question, isMultiSelect, isRequired }, values),
        });

        if (!createdQuestionDefinition?.data?.createFormFieldDefinition?.id) {
          showFeedback();
          return;
        }

        const createFormFieldResponse = await createFormField({
          variables: mapFormFieldRequestBody(
            formId,
            { ...question, isMultiSelect, isRequired, row: addQuestionToRow },
            values,
            createdQuestionDefinition.data.createFormFieldDefinition.id
          ),
        });

        const formFieldResult = checkFormFieldResultResponse(createFormFieldResponse, FormFieldReponseType.CREATE);

        if (onSave) {
          onSave({
            ...question,
            ...createdQuestionDefinition.data.createFormFieldDefinition,
            ...formFieldResult,
          });
        }

        reactiveFeedbackMessage({
          message: 'Question Created',
          isError: false,
          autoclose: true,
        });
      }
    } catch (e) {
      console.error(e);
      showFeedback();
    }
  };

  return (
    <>
      <div id={`${classPrefix}-question-form`} data-testid={`${classPrefix}-question-form`}>
        <div className={`${classPrefix}-question-form__content`}>
          <div className={`${classPrefix}-question-form__section`}>
            <div className={`${classPrefix}-question-form__fields`}>
              {question && <h3>Attributes</h3>}

              <div
                data-testid={`${classPrefix}-label-input-container`}
                className={`${classPrefix}-question-form__field`}
              >
                <CreateElement
                  inputRef={register({
                    required: 'You must enter a label for the question.',
                  })}
                  useHookResponse={questionForm}
                  type="text"
                  field="label"
                  label={
                    <>
                      Question Label<span className="u-colorNegative">*</span>
                    </>
                  }
                  placeholder="Label"
                  autoComplete="off"
                />
                <small>The question label is displayed on the registration (e.g. First Name, Grade, etc).</small>
              </div>
              <div
                data-testid={`${classPrefix}-name-input-container`}
                className={`${classPrefix}-question-form__field`}
              >
                <CreateElement
                  inputRef={register({
                    required: 'You must enter a reporting identifier for the question.',
                    validate: {
                      value: (value) => {
                        const pattern = /^[a-zA-Z0-9_\s]*$/;
                        if (!pattern.test(value) && question?.name !== 'first_name' && question?.name !== 'last_name') {
                          return 'This field can only contain letters and numbers';
                        }
                        if (classPrefix === 'edit') {
                          return true;
                        }

                        const found = questionDefinitions?.data?.organizationFormFieldDefinitions.find(
                          (q) =>
                            q.name.replace(/ /g, '').toLowerCase().trim() ===
                              value.replace(/ /g, '').toLowerCase().trim() ||
                            q.name.replace(/_/g, '').toLowerCase().trim() ===
                              value.replace(/_/g, '').toLowerCase().trim() ||
                            toCamelCase(q.name.trim()) === toCamelCase(value.trim()) ||
                            toCamelCase(q.name.trim()) === value.trim() ||
                            value.replace(/_/g, '').toLowerCase().trim() === 'name' ||
                            value.replace(/ /g, '').toLowerCase().trim() === 'name'
                        );

                        return found
                          ? `Your "Reporting Identifier" must be unique. "${value}" is already in use.`
                          : true;
                      },
                    },
                  })}
                  useHookResponse={questionForm}
                  type="text"
                  field="name"
                  label={
                    <>
                      Reporting Identifier<span className="u-colorNegative">*</span>
                    </>
                  }
                  placeholder="Name"
                  autoComplete="off"
                  disabled={!!question}
                />
                <small>
                  Reporting identifiers are used in your registration reports. They can only contain numbers and
                  letters, with either a space or underscore.
                </small>
              </div>

              <div className={`${classPrefix}-question-form__field`}>
                <CreateElement
                  inputRef={register({
                    required: 'You must select a type for the question.',
                  })}
                  useHookResponse={questionForm}
                  type="select"
                  field="type"
                  label={
                    <>
                      Question Type<span className="u-colorNegative">*</span>
                    </>
                  }
                  placeholder="Type"
                  autoComplete="off"
                  disabled={!!question}
                  options={[
                    {
                      text: 'Short Answer',
                      value: 'text',
                    },
                    {
                      text: 'Paragraph',
                      value: 'textarea',
                    },
                    {
                      text: 'Dropdown',
                      value: 'select',
                    },
                    {
                      text: 'Email',
                      value: 'email',
                    },
                    {
                      text: 'Date',
                      value: 'date',
                    },
                    {
                      text: 'Phone Number',
                      value: 'phone',
                    },
                    {
                      text: 'File Upload',
                      value: 'file',
                    },
                  ]}
                />
              </div>
              <div className={`${classPrefix}-question-form__field`}>
                {hasOptions && (
                  <>
                    <CreateElement
                      inputRef={register({
                        required: 'You must enter options for the question.',
                      })}
                      useHookResponse={questionForm}
                      disabled={question && !question?.organization_id ? true : false}
                      type="textarea"
                      field="options"
                      label={
                        <>
                          Answer Options<span className="u-colorNegative">*</span>
                        </>
                      }
                      spellCheck={false}
                      autoComplete="off"
                    />
                    <small>Enter each option that registrants can select from on a new line.</small>
                  </>
                )}
              </div>

              {watchType === 'file' && (
                <div className={`${classPrefix}-question-form__field`}>
                  <FieldLabel name="firstName">Help Text</FieldLabel>
                  <Editor
                    initialValue={question?.helpText}
                    onChange={(e) => setValue('helpText', e.target.getContent())}
                    apiKey={TINY_MCE_API_KEY}
                    init={{
                      menubar: false,
                      resize: false,
                      preview_styles: false,
                      relative_urls: false,
                      convert_urls: false,
                      height: '100',
                      statusbar: false,
                      content_style: 'body { font-size: 14px; }',
                      plugins: 'textpattern link',
                      contextmenu: 'undo redo | cut copy paste',
                      toolbar_mode: 'wrap',
                      toolbar: 'undo redo | cut copy paste | link unlink',
                    }}
                  />
                  <FieldMessage status={helpTextError ? 'error' : undefined}>400 max character limit</FieldMessage>
                </div>
              )}

              {question?.organization_id && (
                <div className={`${classPrefix}-question-form__field`}>
                  <CreateElement
                    inputRef={register({})}
                    useHookResponse={questionForm}
                    type="checkbox"
                    field="saveToLibrary"
                    label="Sync changes to your question library."
                  />
                  <small>Leave unchecked to apply changes to this question for this registration only.</small>
                </div>
              )}

              {watchType === 'select' && (
                <div className={`${classPrefix}-question-form__field u-flex`}>
                  <div className="label u-spaceRightLg">
                    <strong>Select All That Apply</strong> <span className="u-colorNegative">*</span>
                  </div>
                  <Toggle
                    name="multi-select"
                    mods={`${question ? 'disabled' : ''}`}
                    inputProps={{
                      checked: isMultiSelect,
                      onClick: () => setIsMultiselect(!isMultiSelect),
                      disabled: !!question,
                    }}
                  />
                </div>
              )}
            </div>

            {question && !disableEditingAttributes && (
              <>
                <div className="edit-question-form__fields u-spaceTopLg">
                  <h3>Question Requirement</h3>

                  <div className="edit-question-form__field">
                    <Button
                      type="button"
                      mods={`u-borderNone u-borderRadiusNone ${
                        isRequired ? 'u-colorSuccess6 u-bgNeutral2' : 'u-colorNeutral7'
                      }`}
                      onClick={() => setIsRequired(true)}
                      isDisabled={question?.locked ?? false}
                      testId="question-form-required"
                    >
                      {isRequired && <Icon name="check" mods="u-fontSizeXs u-spaceRightXs" />}
                      Required
                    </Button>
                    {!question.locked && (
                      <Button
                        type="button"
                        mods={`u-borderNone u-borderRadiudNone ${
                          isRequired ? 'u-colorNeutral7' : 'u-colorSuccess6 u-bgNeutral2'
                        }`}
                        onClick={() => setIsRequired(false)}
                        isDisabled={question?.locked ?? false}
                        testId="question-form-optional"
                      >
                        {!isRequired && <Icon name="check" mods="u-fontSizeXs u-spaceRightXs" />}
                        Optional
                      </Button>
                    )}
                  </div>
                </div>
                <div className="edit-question-form__fields u-spaceTopLg">
                  <h3>Custom Rules</h3>
                  <AdditionalQuestionConditionalLogic
                    orgId={orgId}
                    formId={formId}
                    question={question}
                    formHook={questionForm}
                    visibilityRules={visibilityRules}
                  />
                </div>
                {question.type === 'select' && (
                  <div className="edit-question-form__fields u-spaceTopLg">
                    <h3>Response based fees and discounts</h3>
                    <AddOnFees
                      orgId={orgId}
                      formId={formId}
                      question={question}
                      formHook={questionForm}
                      savedAddOnFees={formFees}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <div data-testid={`${classPrefix}-question-form__actions`} className={`${classPrefix}-question-form__actions`}>
        <Button
          type="button"
          mods="u-spaceRightSm"
          testId="question-form-cancel"
          onClick={() => {
            if (dirty) {
              setShowWarning(true);
            } else {
              onCancel();
            }
          }}
        >
          Cancel
        </Button>
        <Button
          mods="Button Button--primary"
          type="button"
          testId="question-form-save"
          onClick={handleSubmit(saveQuestion)}
        >
          Save
        </Button>
      </div>
      <WarningModal
        show={showWarning}
        onClose={() => setShowWarning(false)}
        onSubmit={() => {
          setShowWarning(false);
          onCancel();
        }}
      >
        <p className="u-padEndsSm">All unsaved changes will be lost.</p>
      </WarningModal>
    </>
  );
};

export default AdditionalQuestionForm;
